// Shared tooltip utilities used across different chart types

export function percentageOfResponses(item, positionCount, totalFieldCount) {
  if (item[totalFieldCount] === 0) return "0%";

  const count = item[positionCount];
  if (count === null || count === undefined) return "0%";

  return `${Math.round((100 * count) / item[totalFieldCount])}%`;
}

export function dataRowHtml(label, formattedValue, comparison = false, color) {
  return `
    <div class='flex items-center text-gray-700 text-tiny pt-1'>
      <div class='h-2 w-2 rounded-full ${
        comparison ? "bg-gray-400" : "bg-indigo-400"
      }' ${color ? "style='background-color:" + color + ";'" : ""}>
      </div>
      <div class='ml-1.5 grow'>
        ${window.scrubHtml(label)}
      </div>
      <div class='ml-12'>
        ${formattedValue}
      </div>
    </div>
  `;
}

export function valueDifferenceHtml(newValue, originalValue, lowerIsBetter) {
  const diff = newValue - originalValue;
  let colors, value;
  if (Math.abs(diff) < 0.01) {
    colors = "text-gray-500";
    value = `0.00`;
  } else if ((lowerIsBetter && diff < 0) || (!lowerIsBetter && diff > 0)) {
    // Improvement
    colors = "comparison-badge-above";
    value = `${diff > 0 ? "+" : ""}${diff.toFixed(2)}`;
  } else {
    // Decline
    colors = "comparison-badge-below";
    value = `${diff > 0 ? "+" : ""}${diff.toFixed(2)}`;
  }
  return `
    <div class='border text-2xs font-medium rounded px-1.5 py-px ${colors}'>
      ${value}
    </div>
  `;
}

export function percentChangeHtml(newValue, originalValue, invert = false) {
  const pct = percentChange(newValue, originalValue);

  let colors, value;

  if (pct === 0) {
    colors = "text-gray-500";
    value = `0.0`;
  } else if ((pct > 0 && !invert) || (pct < 0 && invert)) {
    colors = "comparison-badge-above";
    value = `${pct > 0 ? "+" : ""}${pct.toFixed(1)}`;
  } else {
    colors = "comparison-badge-below";
    value = `${pct > 0 ? "+" : ""}${pct.toFixed(1)}`;
  }

  return `
    <div class='border text-2xs font-medium rounded px-1.5 py-px ${colors}'>
      ${value}%
    </div>
  `;
}

function percentChange(newValue, originalValue) {
  if (originalValue === 0) return 0; // avoid dividing by zero

  const percentage = newValue / originalValue;

  return 100.0 * (percentage - 1);
}

/**
 * Renders a standardized tooltip with survey item details and distribution
 * @param {Object} config - Configuration object
 * @param {string} config.itemTypeLabel - Label for the item type (e.g., "Workflow", "Internal Tool")
 * @param {string} config.surveyText - The survey question text
 * @param {string} config.scoringMethod - The scoring method description
 * @param {boolean} config.hideScoringMethod - If true, hide the scoring method section
 * @param {Array} config.scaleItems - Array of {index, countField} objects for scale positions
 * @param {Array} config.options - Array of option labels
 * @param {Object} item - The data item containing counts
 * @param {string} totalFieldName - The field name to use for total count (e.g., "count", "sample_size")
 * @returns {string} HTML string for the tooltip
 */
export function renderTooltip(config, item, totalFieldName = "count") {
  const {
    itemTypeLabel,
    surveyText,
    scoringMethod,
    scaleItems,
    options,
    hideScoringMethod,
  } = config;

  // Helper to check if a position should be rendered (has option label)
  const shouldRenderPosition = (countField, index) => {
    // Only show positions that have a label defined in options
    // This filters out unused positions (e.g., position 5-6 for a 5-point scale)
    return (
      options[index] !== undefined &&
      options[index] !== null &&
      options[index] !== ""
    );
  };

  // Helper function to render a position row
  const renderPosition = (index, countField) => {
    const label = options[index] || `Position ${index}`;
    return `
      <div class="h-5 flex items-center justify-between">
        <div class="w-4/12 text-right pr-2.5 text-xs text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap">${window.scrubHtml(label)}</div>
        <div class="flex items-center grow w-8/12 rounded-full overflow-hidden">
          <div class="h-1 py-px bg-gray-400 rounded-full" style="width: ${percentageOfResponses(item, countField, totalFieldName)}"></div>
          <div class="w-8 ml-2 text-gray-500 text-3xs">${percentageOfResponses(item, countField, totalFieldName)}</div>
        </div>
      </div>
    `;
  };

  const distributionRows = scaleItems
    .filter(({ countField, index }) => shouldRenderPosition(countField, index))
    .map(({ index, countField }) => renderPosition(index, countField))
    .join("");

  return `
    <div class="flex p-2 text-sm bg-white text-left shadow-lg rounded-lg border">
      <div class="w-64 pr-5 text-xs bg-white text-left border-r border-gray-100">
        <div class="mb-1.5 text-gray-400">
          ${itemTypeLabel}
        </div>
        <div class="mt-1 text-gray-700">
          ${window.scrubHtml(surveyText)}
        </div>
        ${
          !hideScoringMethod
            ? `
        <div class="mt-5 text-gray-400">
          Scoring method
        </div>
        <div class="mt-1 text-gray-700">
          ${scoringMethod}
        </div>
        `
            : ""
        }
      </div>
      <div class="w-64 ml-5">
        <div class="text-xs text-gray-500 mb-2.5">
          Distribution
        </div>
        ${distributionRows}
      </div>
    </div>
  `;
}
